import { FC, useCallback, useMemo } from 'react';
import { Breadcrumbs, Typography } from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';

import { RoutePath } from 'constants/RoutePath';
import { useSelector } from 'react-redux';
import { accountSelector, serviceSelector } from 'store/auth/authSelectors';

import './Breadcrumb.scss';

export interface IBreadCrumbItem {
  label: string;
  path?: RoutePath | string;
  hidden?: boolean;
}

interface IComponentProps {
  items: IBreadCrumbItem[];
  hideNavToPev?: boolean;
}

const Breadcrumb: FC<IComponentProps> = ({
  items,
  hideNavToPev = false,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const account = useSelector(accountSelector);
  const service = useSelector(serviceSelector);

  const prevIndex = useMemo(() => {
    const index = items.findLastIndex((item) => item.path && !item.hidden);
    return index > 0 ? index : 0;
  }, [pathname, items]);

  const navigateToPrev = useCallback(() => {
    const path = items[prevIndex]?.path;
    if (path) {
      navigate(`${account?.shortName}/${service?.shortName}${path}`, { state: { back: true } });
    }
  }, [items, prevIndex, account, service]);

  return (
    <>
      {!hideNavToPev && (
      <FontAwesomeIcon
        icon={faChevronLeft}
        className="breadcrumb__back-btn"
        onClick={navigateToPrev}
        data-testid="nav-prev-id"
      />
      )}
      <Breadcrumbs aria-label="breadcrumb" className="breadcrumb">
        {items.map((item) => (
          !item.hidden && (item.path ? (
            <Link
              key={item.path}
              to={`${account?.shortName}/${service?.shortName}${item.path}`}
              className="breadcrumb__item"
              data-testid={`${item.label}-link-id`}
              state={{ back: true }}
            >
              {item.label}
            </Link>
          ) : (
            <Typography
              key={item.label}
              className="breadcrumb__item selected"
              data-testid={`${item.label}-text-id`}
            >
              {item.label}
            </Typography>
          )
          )))}
      </Breadcrumbs>
    </>
  );
};

export default Breadcrumb;
